import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { RefLabel } from '@/components/refLabel';
import { getNodeTestIdProp } from '@/utils';
import type { ProductBatch } from '@/api/models';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import {
    formatDate,
    formatMoney,
    formatNumber,
    orDash,
    stockScopeCopy,
} from './productDetailMeta';
import { asCell, asColumnHeader, asHeading4 } from './productDetailTypography';

/** The table's columns, in order; the figures sit at the end of theirs. */
const BATCH_COLUMNS: { labelKey: string; align: 'start' | 'end' }[] = [
    { labelKey: 'products.detail.batches.number', align: 'start' },
    { labelKey: 'products.detail.batches.quantity', align: 'end' },
    { labelKey: 'products.detail.batches.purchasePrice', align: 'end' },
    { labelKey: 'products.detail.batches.supplier', align: 'start' },
    { labelKey: 'products.detail.batches.received', align: 'start' },
    { labelKey: 'products.detail.batches.expiry', align: 'start' },
];

type ProductVariantBatchesProps = {
    variantId: string;
    batches: ProductBatch[];
    /** Whose stock the batches are, which the heading names (bos-app#119). */
    stockFigures: StockFiguresScope;
};

/**
 * An open variant's batches: a heading with the count, then a table of them,
 * or a line saying there are none. The text is HeroUI Typography, each cell
 * rendered as its `th` or `td`; figures take `tabular-nums` (the registered
 * §6 gap). The row rules still come from `className` on the raw table, which
 * `.agents/frontend.md` registers until it becomes a HeroUI Table (§6).
 */
export const ProductVariantBatches: React.FC<ProductVariantBatchesProps> = ({
    variantId,
    batches,
    stockFigures,
}) => {
    // BOS-165: batch dates carry a month *name*, so they have to know the
    // language.
    const { t, i18n } = useTranslation('pages');
    const scope = stockScopeCopy(stockFigures);

    return (
        <div className="flex flex-col gap-2">
            <Typography render={asHeading4} type="body-xs" weight="semibold">
                {t(scope.keys.batches, scope.values)} ({batches.length})
            </Typography>
            {batches.length === 0 ? (
                <Typography
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp(
                        `product-variant-batches-empty-${variantId}`,
                    )}
                >
                    {t('products.detail.batches.empty')}
                </Typography>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                        {/* A caption and `scope="col"` on every header,
                            matching the supplier statement's raw tables.
                            Without them a screen reader reaches a grid of
                            numbers with nothing saying which variant's batches
                            these are, or which column a given cell sits
                            under. */}
                        <caption className="sr-only">
                            {t('products.detail.batches.caption')}
                        </caption>
                        <thead>
                            <tr>
                                {BATCH_COLUMNS.map((column, index) => (
                                    <Typography
                                        align={column.align}
                                        className={
                                            index < BATCH_COLUMNS.length - 1
                                                ? 'py-2 pr-4'
                                                : 'py-2'
                                        }
                                        color="muted"
                                        key={column.labelKey}
                                        render={asColumnHeader}
                                        type="body-xs"
                                        weight="medium"
                                    >
                                        {t(column.labelKey)}
                                    </Typography>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {batches.map((batch) => (
                                <tr
                                    className="border-t border-separator"
                                    key={batch.id}
                                    {...getNodeTestIdProp(
                                        `product-batch-${batch.id}`,
                                    )}
                                >
                                    <Typography
                                        className="py-2 pr-4"
                                        render={asCell}
                                        type="body-sm"
                                    >
                                        {orDash(batch.batchNumber)}
                                    </Typography>
                                    <Typography
                                        align="end"
                                        className="py-2 pr-4 tabular-nums"
                                        render={asCell}
                                        type="body-sm"
                                    >
                                        {formatNumber(batch.quantity)}
                                    </Typography>
                                    <Typography
                                        align="end"
                                        className="py-2 pr-4 tabular-nums"
                                        render={asCell}
                                        type="body-sm"
                                    >
                                        {formatMoney(batch.purchasePriceTetri)}
                                    </Typography>
                                    <Typography
                                        className="py-2 pr-4"
                                        render={asCell}
                                        type="body-sm"
                                    >
                                        {batch.supplier ? (
                                            <RefLabel
                                                archived={
                                                    batch.supplier.archived
                                                }
                                                deleted={batch.supplier.deleted}
                                                id={batch.supplier.id}
                                                name={batch.supplier.name}
                                            />
                                        ) : (
                                            orDash(null)
                                        )}
                                    </Typography>
                                    <Typography
                                        className="py-2 pr-4"
                                        render={asCell}
                                        type="body-sm"
                                    >
                                        {formatDate(
                                            batch.receivedDate,
                                            i18n.language,
                                        )}
                                    </Typography>
                                    <Typography
                                        className="py-2"
                                        render={asCell}
                                        type="body-sm"
                                    >
                                        {formatDate(
                                            batch.expiryDate,
                                            i18n.language,
                                        )}
                                    </Typography>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
